import { cn } from '../ui/utils';
import type { CaseStatus } from '../../types';
import { CASE_STATUS_CONFIG } from '../../types';

interface CaseStatusBadgeProps {
  status: CaseStatus;
  className?: string;
}

// Darwin Design System "Status" chip (Figma node 5306-12097) — the same chip
// used by sop-studio (StatusBadge) and taskline (ui/status): a small 2px-radius
// chip with a 12px leading icon + 12px regular uppercase label, off the Darwin
// color ramps. RedactIt's case statuses keep their established Darwin hues
// (in-progress = teal/info, completed = chartreuse green/success, not-started =
// neutral cool-slate).
type Look = 'progress' | 'done' | 'neutral';

const LOOK: Record<CaseStatus, Look> = {
  not_started: 'neutral',
  in_progress: 'progress',
  completed: 'done',
};

// bg + text per look, straight off the Darwin ramps in tailwind.config.js
// (Teal/200·Teal/800, Green/200·Green/800, neutral Primary/100 + border).
const STYLES: Record<Look, string> = {
  progress: 'bg-teal-200 text-teal-800',
  done: 'bg-green-200 text-green-800',
  neutral: 'border border-gray-200 bg-gray-100 text-gray-700',
};

function StatusIcon({ look }: { look: Look }) {
  const common = {
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    className: 'h-3 w-3 shrink-0',
    'aria-hidden': true,
  } as const;

  if (look === 'done') {
    // check-circle (DS "closed/done")
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.25" />
        <path d="M5.25 8.25 7 10 10.75 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (look === 'progress') {
    // contrast / half-filled circle (DS "in progress")
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.25" />
        <path d="M8 1.75A6.25 6.25 0 0 1 8 14.25Z" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  // hollow circle (DS "new / not started")
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="6.25" />
    </svg>
  );
}

export function CaseStatusBadge({ status, className }: CaseStatusBadgeProps) {
  const look = LOOK[status];

  return (
    <span
      className={cn(
        'inline-flex w-fit items-center justify-center gap-1 rounded-sm p-[5px] text-[12px] font-normal uppercase leading-none whitespace-nowrap',
        STYLES[look],
        className,
      )}
    >
      <StatusIcon look={look} />
      {CASE_STATUS_CONFIG[status].label}
    </span>
  );
}
